import React from 'react';
import './styles.scss';

export interface InsightCardProps {
  children: React.ReactNode;
  onClick?: () => void;
  isActive?: boolean;
  className?: string;
  ariaLabel?: string;
}

export interface InsightCardTitleProps {
  children: React.ReactNode;
  className?: string;
}

export interface InsightCardSubtitleProps {
  children: React.ReactNode;
  className?: string;
}

export interface InsightCardValueProps {
  children: React.ReactNode;
  className?: string;
}

export interface InsightCardValueNumberProps {
  children: React.ReactNode;
  className?: string;
}

export interface InsightCardValueUnitProps {
  children: React.ReactNode;
  className?: string;
}

export interface InsightCardMetaProps {
  children: React.ReactNode;
  className?: string;
}

export interface InsightCardDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

const InsightCardTitle = ({ children, className }: InsightCardTitleProps) => {
  const titleClassName = ['insight-card__title', className ?? ''].filter(Boolean).join(' ');
  return <div className={titleClassName}>{children}</div>;
};

const InsightCardSubtitle = ({ children, className }: InsightCardSubtitleProps) => {
  const subtitleClassName = ['insight-card__subtitle', className ?? ''].filter(Boolean).join(' ');
  return <div className={subtitleClassName}>{children}</div>;
};

const InsightCardValue = ({ children, className }: InsightCardValueProps) => {
  const valueClassName = ['insight-card__value', className ?? ''].filter(Boolean).join(' ');
  return <div className={valueClassName}>{children}</div>;
};

const InsightCardValueNumber = ({ children, className }: InsightCardValueNumberProps) => {
  const valueNumberClassName = ['insight-card__value-number', className ?? ''].filter(Boolean).join(' ');
  return <span className={valueNumberClassName}>{children}</span>;
};

const InsightCardValueUnit = ({ children, className }: InsightCardValueUnitProps) => {
  const valueUnitClassName = ['insight-card__value-unit', className ?? ''].filter(Boolean).join(' ');
  return <span className={valueUnitClassName}>{children}</span>;
};

const InsightCardMeta = ({ children, className }: InsightCardMetaProps) => {
  const metaClassName = ['insight-card__meta', className ?? ''].filter(Boolean).join(' ');
  return <div className={metaClassName}>{children}</div>;
};

const InsightCardDescription = ({ children, className }: InsightCardDescriptionProps) => {
  const descriptionClassName = ['insight-card__description', className ?? ''].filter(Boolean).join(' ');
  return <div className={descriptionClassName}>{children}</div>;
};

type InsightCardCompoundComponent = React.FC<InsightCardProps> & {
  Title: React.FC<InsightCardTitleProps>;
  Subtitle: React.FC<InsightCardSubtitleProps>;
  Value: React.FC<InsightCardValueProps>;
  ValueNumber: React.FC<InsightCardValueNumberProps>;
  ValueUnit: React.FC<InsightCardValueUnitProps>;
  Meta: React.FC<InsightCardMetaProps>;
  Description: React.FC<InsightCardDescriptionProps>;
};

const InsightCardRoot: InsightCardCompoundComponent = ({
  children,
  onClick,
  isActive = false,
  className,
  ariaLabel,
}: InsightCardProps) => {
  const isInteractive = typeof onClick === 'function';
  const rootClassName = [
    'insight-card',
    isInteractive ? 'insight-card--interactive' : '',
    isActive ? 'insight-card--active' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  if (isInteractive) {
    return (
      <button
        type="button"
        className={rootClassName}
        onClick={onClick}
        aria-pressed={isActive || undefined}
        aria-label={ariaLabel}
      >
        {children}
      </button>
    );
  }

  return <div className={rootClassName}>{children}</div>;
};

InsightCardRoot.Title = InsightCardTitle;
InsightCardRoot.Subtitle = InsightCardSubtitle;
InsightCardRoot.Value = InsightCardValue;
InsightCardRoot.ValueNumber = InsightCardValueNumber;
InsightCardRoot.ValueUnit = InsightCardValueUnit;
InsightCardRoot.Meta = InsightCardMeta;
InsightCardRoot.Description = InsightCardDescription;

export const InsightCard = InsightCardRoot;
export default InsightCardRoot;
